import { ColumnDef } from "@tanstack/react-table";
import { Link } from "@heroui/link";
import type { TFunction } from "i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import { getAuditColumns } from "@/features/Audit/getAuditColumns";

import { formatNumber } from "@/lib/utils";

import { CheckIcon } from "@radix-ui/react-icons";

export type A7OPCVMIssuerTS = {
  id: number;
  date: Date;
  a7_opcvm_strategy_id: number;
  a7_opcvm_underlying_id: number;
  a7_opcvm_time_serie_file_id: number;
  valorisation_part_du_sous_jacent: number;
  detention_du_fonds_dans_le_sous_jacent: number;
  version: number;
  a7_opcvm_issuer_id?: number;
};

const tableName = "a7_opcvm_issuer_ts";

export const getIssuerTsColumns = (
  t: TFunction,
): ColumnDef<A7OPCVMIssuerTS>[] => {
  const tTable = (key: string, defaultValue: string) =>
    t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });
  const tCommon = (key: string, defaultValue: string) =>
    t(`actions.${key}`, { ns: "common", defaultValue });

  return [
    {
      accessorKey: "id",
      header: tTable("id", "ID"),
    },
    {
      accessorKey: "date",
      header: tTable("date", "Date"),
    },
    {
      accessorKey: "a7_opcvm_issuer_name",
      header: tTable("a7_opcvm_issuer_name", "Issuer"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-opcvm/a7_opcvm_issuers/${row.original.a7_opcvm_issuer_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_opcvm_issuer_name")}
          </Link>
        );
      },
    },
    {
      accessorKey: "secteur_nace",
      header: tTable("secteur_nace", "Sector"),
    },
    {
      accessorKey: "identifiant_fiscal_du_sous_jacent",
      header: tTable("identifiant_fiscal_du_sous_jacent", "Tax identifier"),
    },
    {
      accessorKey: "chiffre_d_affaires_du_sous_jacent",
      header: () => (
        <div className="text-right">
          {tTable("chiffre_d_affaires_du_sous_jacent", "Revenue")}
        </div>
      ),
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          value != null && (
            <div className="text-right">{formatNumber(value as number)}</div>
          )
        );
      },
    },
    {
      accessorKey: "nb_employes_contrepartie",
      header: () => (
        <div className="text-right">
          {tTable("nb_employes_contrepartie", "Employees")}
        </div>
      ),
    },
    {
      accessorKey: "actionnaire_majoritaire_public",
      header: tTable("actionnaire_majoritaire_public", "Majority shareholder"),
      cell: ({ getValue }) => getValue() == "YES" && <CheckIcon />,
    },
    {
      accessorKey: "notation_moodys_contrepartie",
      header: tTable("notation_moodys_contrepartie", "Moody's rating"),
    },
    {
      accessorKey: "notation_fitch_contrepartie",
      header: tTable("notation_fitch_contrepartie", "Fitch rating"),
    },
    {
      accessorKey: "notation_sp_contrepartie",
      header: tTable("notation_sp_contrepartie", "S&P rating"),
    },
    {
      accessorKey: "cqs_contrepartie",
      header: tTable("cqs_contrepartie", "CQS"),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        return (
          <div className="flex justify-end">
            <Link
              href={`${window.location.pathname}/${row.getValue("id")}`}
              className="text-xs"
              underline="hover"
            >
              {tCommon("edit", "Edit")}
            </Link>
          </div>
        );
      },
    },
  ];
};

export const getIssuerTsAuditColumns = (t: TFunction) =>
  getAuditColumns<A7OPCVMIssuerTS>(getIssuerTsColumns(t)).filter(
    (column) =>
      ("accessorKey" in column
        ? String((column as { accessorKey?: unknown }).accessorKey ?? "")
        : "") !== "a7_opcvm_issuer_name",
  );
